import { DeliveryType } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import {
  Box,
  Button,
  Divider,
  Grid,
  Group,
  rem,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import dayjs, { locale } from 'dayjs';
import 'dayjs/locale/vi';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';
import type { SameDayHourSuggest, TimeSlot } from '../../api/service';
import { useSamedayHourSuggestMutation } from '../../api/service/use-sameday-hour-suggest';
import BestHourGrayIcon from '@/assets/icons/best-hour-gray.svg';
import BestHourOrangeIcon from '@/assets/icons/best-hour-orange.svg';
import CustomImage from '@/components/custom-image';
import { useRemoteConfigHook } from '@/hooks';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

locale('vi');
locale('en');

type SameDayTabKey = {
  key: string;
  label: {
    title: string;
    description: string;
  };
  slots: TimeSlot[];
};

const BEST_HOUR = 'best_hour';

type BookingSamedayHourProps = {
  onSchedule: (orderTime: number) => void;
};

const BookingSamedayHour = ({ onSchedule }: BookingSamedayHourProps) => {
  const tPickupTime = useTranslations('booking.pickup_time');
  const tCommon = useTranslations('common');
  const isMobile = useIsMobile();
  const {
    selectedService,
    estimatedRequestData,
    orderTime,
    samedaySelectedTime,
    setSamedaySelectedTime,
  } = useBookingStore((state) => state);
  const { surgePriceConfig } = useRemoteConfigHook();
  const [dayTabs, setDayTabs] = useState<SameDayTabKey[]>([]);
  const [activeTab, setActiveTab] = useState<string>(BEST_HOUR);
  const [tempSelectedTime, setTempSelectedTime] = useState<TimeSlot | null>(
    samedaySelectedTime
  );

  const today = dayjs();

  const { mutate: samedayHourSuggestMutation, isPending: isLoading } =
    useSamedayHourSuggestMutation({
      onSuccess(data) {
        const daysLocalized = createTabList(data);
        setDayTabs(daysLocalized);
      },
      onError(error) {
        console.error('Sameday hour suggest error:', error);
      },
    });

  const createTabList = (data: SameDayHourSuggest[]): SameDayTabKey[] => {
    const keys = Object.keys(data);

    const list = keys.map((key) => {
      if (key === BEST_HOUR) {
        return {
          key,
          label: {
            title: tPickupTime(BEST_HOUR),
            description: 'best_hour_icon',
          },
          slots: data[key] || [],
        };
      }

      const date = dayjs(key);

      return {
        key,
        label: {
          title: date.isSame(today, 'day')
            ? tPickupTime('today')
            : date.format('dddd').toLowerCase(),
          description: date.format('D'), // e.g. "23"
        },
        slots: data[key] || [],
      };
    });

    // Ensure 'best_hour' is always at the beginning
    return [
      ...list.filter((item) => item.key === BEST_HOUR),
      ...list.filter((item) => item.key !== BEST_HOUR),
    ];
  };

  useEffect(() => {
    if (isLoading) return;

    if (
      selectedService?.service_hours &&
      estimatedRequestData?.path &&
      !selectedService?.suggest_earliest_block_hour
    ) {
      const params = {
        service_id: selectedService._id,
        path: estimatedRequestData.path,
      };

      samedayHourSuggestMutation(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    estimatedRequestData?.path,
    selectedService?._id,
    selectedService?.suggest_earliest_block_hour,
  ]);

  // Auto-select logic when dayTabs changes
  useEffect(() => {
    if (dayTabs.length === 0) return;

    if (orderTime) {
      // Auto-select the time slot that matches orderTime
      const matchingSlot = dayTabs
        .flatMap((tab) => tab.slots)
        .find((slot) => slot.est_time === orderTime);

      if (matchingSlot) {
        setTempSelectedTime(matchingSlot);

        // Find and set the active tab that contains this time slot
        const matchingTab = dayTabs.find((tab) =>
          tab.slots.some((slot) => slot.est_time === orderTime)
        );

        if (matchingTab) {
          setActiveTab(matchingTab.key);
        }
      } else {
        // auto-select the first tab if no match found
        setActiveTab(dayTabs[0].key);
        const firstTab = dayTabs[0];
        if (firstTab.slots.length > 0) {
          setTempSelectedTime(firstTab.slots[0]);
        }
      }
    } else {
      // If no orderTime, auto-select best_hour tab and first item
      setActiveTab(BEST_HOUR);
      const bestHourTab = dayTabs.find((tab) => tab.key === BEST_HOUR);
      if (bestHourTab && bestHourTab.slots.length > 0) {
        setTempSelectedTime(bestHourTab.slots[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayTabs, orderTime]);

  const formatTimeSlots = (time: TimeSlot['start_time']) => {
    return dayjs().startOf('day').add(time, 'minute').format('HH:mm');
  };

  const slotsActiveTab = useMemo(
    () => dayTabs.find((day) => day.key === activeTab)?.slots,
    [dayTabs, activeTab]
  );

  const getLocalizedTime = (
    slot: TimeSlot,
    key: SameDayTabKey['key']
  ): string | React.ReactNode => {
    const {
      start_time: startTime,
      end_time: endTime,
      est_date_time: estDateTime,
    } = slot;

    if (startTime === endTime) {
      return (
        <span
          style={{
            textTransform: 'initial',
          }}
        >
          {getPickupNowLabel(selectedService)}
        </span>
      );
    }

    if (key === BEST_HOUR) {
      const isToday = dayjs(estDateTime).isSame(dayjs(), 'day');
      const dayLabel = isToday
        ? tPickupTime('today')
        : dayjs(estDateTime).locale('vi').format('dddd');
      return `${dayLabel}, ${formatTimeSlots(startTime)} - ${formatTimeSlots(endTime)}`;
    }

    return `${formatTimeSlots(startTime)} - ${formatTimeSlots(endTime)}`;
  };

  const handleSelectTimeSlot = (slot: TimeSlot) => {
    if (!slot.est_time) return;

    setTempSelectedTime(slot);
  };

  const handleChangeTab = (key: string) => {
    setActiveTab(key);
    setTempSelectedTime(null);
  };

  const handleConfirmSchedule = () => {
    if (!tempSelectedTime) {
      toast.error({
        withIcon: true,
        title: tPickupTime('error.message'),
      });
      return;
    }

    // Commit the temporary selection to global state
    setSamedaySelectedTime(tempSelectedTime);

    onSchedule(tempSelectedTime.est_time);
  };

  const getPickupNowLabel = (service) => {
    if (!service || service._id.includes(DeliveryType.SameDay)) {
      return tPickupTime('pickup_now_sameday');
    }
    if (service._id.includes('SAMEPRICE')) {
      return tPickupTime('pickup_now_sameprice');
    }
    return tPickupTime('pickup_now_sameday');
  };

  const handleSurgeIcon = (slot: TimeSlot): string | null => {
    if (!surgePriceConfig) return null;

    const deliveryType = selectedService?.delivery_type;
    const suggestedSurgeRate = slot.surge_rate;

    const filteredSurgePriceByConfig: {
      delivery_type: string;
      surge_rate: number;
      surge_rate_decrease?: number;
      surge_rate_increase?: number;
    }[] = surgePriceConfig?.filter(
      (config) => config.delivery_type === deliveryType
    );

    if (
      !filteredSurgePriceByConfig ||
      filteredSurgePriceByConfig.length === 0
    ) {
      return null;
    }

    const surgeRateIncrease =
      filteredSurgePriceByConfig[0].surge_rate_increase ?? 0;

    const surgeRateDecrease =
      filteredSurgePriceByConfig[0].surge_rate_decrease ?? 0;

    if (suggestedSurgeRate < surgeRateDecrease) {
      return `${assetPrefix}/static/icons/surge-down-icon.svg`;
    }

    if (suggestedSurgeRate > surgeRateIncrease) {
      return `${assetPrefix}/static/icons/surge-up-icon.svg`;
    }

    return null;
  };

  if (isLoading || !dayTabs.length) {
    return <LoadingSamedayHour />;
  }

  return (
    <Stack gap={0}>
      <ScrollArea mih={rem(58)} scrollbars="x">
        <Group gap={rem(18)} w={600} px={rem(16)} py={rem(12)}>
          {dayTabs.map((day) => (
            <Stack
              key={day.key}
              onClick={() => handleChangeTab(day.key)}
              style={{ cursor: 'pointer' }}
            >
              <Text
                size="sm"
                fw={500}
                c={activeTab === day.key ? 'primary.6' : 'neutral.5'}
                style={{ textAlign: 'center', textTransform: 'capitalize' }}
              >
                {day.label.title}
              </Text>

              {day.label.description === 'best_hour_icon' ? (
                <Box
                  style={{
                    textAlign: 'center',
                    height: rem(20),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {activeTab === day.key ? (
                    <BestHourOrangeIcon />
                  ) : (
                    <BestHourGrayIcon />
                  )}
                </Box>
              ) : (
                <Text
                  size={rem(20)}
                  fw={700}
                  c={activeTab === day.key ? 'primary.6' : 'neutral.5'}
                  style={{ textAlign: 'center' }}
                >
                  {day.label.description}
                </Text>
              )}
            </Stack>
          ))}
        </Group>
      </ScrollArea>

      <ScrollArea h={rem(272)} scrollbars="y">
        <Stack p={rem(16)}>
          {slotsActiveTab?.length ? (
            slotsActiveTab.map((slot) => {
              const activeSlot = slot.est_time === tempSelectedTime?.est_time;

              return (
                <Group
                  key={slot.est_time}
                  h={rem(48)}
                  gap={rem(8)}
                  p={rem(12)}
                  justify="space-between"
                  w="100%"
                  style={{
                    cursor: 'pointer',
                    borderRadius: rem(8),
                    border: activeSlot
                      ? `1px solid var(--mantine-color-primary-6)`
                      : 'none',
                    backgroundColor: activeSlot
                      ? 'var(--mantine-color-primary-1)'
                      : 'transparent',
                    transition: 'background-color 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!activeSlot) {
                      e.currentTarget.style.backgroundColor =
                        'var(--mantine-color-neutral-1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!activeSlot) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => handleSelectTimeSlot(slot)}
                >
                  <Text
                    size="md"
                    fw={activeSlot ? 600 : 500}
                    c={activeSlot ? 'primary.6' : 'neutral.7'}
                    truncate
                    style={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {getLocalizedTime(slot, activeTab)}
                  </Text>

                  <Group gap={rem(8)} justify="flex-end" align="center">
                    {handleSurgeIcon(slot) && (
                      <img
                        src={handleSurgeIcon(slot)!}
                        alt="surge-icon"
                        style={{ height: rem(16) }}
                      />
                    )}

                    <Text
                      size="md"
                      fw={activeSlot ? 600 : 500}
                      c={activeSlot ? 'primary.6' : 'neutral.7'}
                    >
                      {formatCurrency(slot.est_price)}
                    </Text>
                  </Group>
                </Group>
              );
            })
          ) : (
            <EmptyBookingSamedayHour />
          )}
        </Stack>
      </ScrollArea>

      <Divider h={2} />

      <Grid p="md" justify="space-between">
        {!isMobile ? (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Button
              variant="outline"
              size="md"
              h={rem(48)}
              fw={700}
              fullWidth
              color="neutral.3"
              styles={{
                label: {
                  color: 'var(--mantine-color-neutral-7)',
                  fontSize: rem(14),
                },
              }}
              onClick={() => {
                setSamedaySelectedTime(null);
                onSchedule(0);
              }}
            >
              {tCommon('actions.cancel')}
            </Button>
          </Grid.Col>
        ) : null}

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Button
            size="md"
            mih={48}
            fullWidth
            fw={700}
            disabled={!tempSelectedTime}
            onClick={handleConfirmSchedule}
          >
            {isMobile
              ? tPickupTime('select_pickup_time')
              : tCommon('actions.save')}
          </Button>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default BookingSamedayHour;

const LoadingSamedayHour = () => {
  return (
    <Stack gap={0}>
      <ScrollArea mih={rem(58)} scrollbars="x">
        <Group gap={rem(18)} w={600} px={rem(16)} py={rem(12)}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Stack key={index}>
              <Skeleton height={14} width={50} radius="sm" />
              <Skeleton height={20} width={30} radius="sm" />
            </Stack>
          ))}
        </Group>
      </ScrollArea>

      <ScrollArea h={rem(272)} scrollbars="y">
        <Stack p={rem(16)} gap="sm">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} height={48} radius="md" />
          ))}
        </Stack>
      </ScrollArea>

      <Divider h={2} />

      <Group wrap="nowrap" w="100%" p="md">
        <Skeleton height={48} width="100%" radius="md" />
        <Skeleton height={48} width="100%" radius="md" />
      </Group>
    </Stack>
  );
};

const EmptyBookingSamedayHour = () => {
  const tPickupTime = useTranslations('booking.pickup_time');

  return (
    <Stack gap={0} align="center">
      <CustomImage
        src="/static/images/empty-time-range.webp"
        alt="empty-time-range"
        width={120}
        height={120}
        style={{ objectFit: 'contain' }}
        loading="lazy"
      />
      <Stack gap={rem(4)} align="center">
        <Text size="md" c="neutral.7" fw={700}>
          {tPickupTime('empty.title')}
        </Text>
        <Text size="sm" c="neutral.6" fw={500}>
          {tPickupTime('empty.description')}
        </Text>
      </Stack>
    </Stack>
  );
};
